import { Builder, BuilderContent } from "@builder.io/sdk";

/**
 * Fetches all entries for a given model from Builder.io by paginating through results
 * @param builder - Builder.io SDK instance
 * @param model - Name of the model to fetch content for (e.g. "blog", "page")
 * @returns Promise that resolves to array of all BuilderContent entries for the model
 * @example
 * // Get all blog posts
 * const allPosts = await getAllEntries(builder, "blog");
 *
 * // Get all pages
 * const allPages = await getAllEntries(builder, "page");
 */
export const getAllEntries = async (builder: Builder, model: string) => {
  let allEntries: BuilderContent[] = [];
  let offset = 0;
  let hasMore = true;

  while (hasMore) {
    const entries = await builder.getAll(model, {
      limit: 1000,
      offset: offset,
      cachebust: true,
      prerender: false,
      options: {
        enrich: true,
        noTargeting: true,
        includeUnpublished: false,
      },
      includeUnpublished: false,
    });

    if (entries.length === 0) {
      hasMore = false;
    } else {
      allEntries = [...allEntries, ...entries];
      offset += 100;
    }
  }

  return allEntries;
};

/**
 * Fetches paginated entries for a given model from Builder.io
 * @param builder - Builder.io SDK instance
 * @param model - Name of the model to fetch content for (e.g. "blog", "event-post", "class", "class-type", "event-type", "knowledge-base", "knowledge-document-type")
 * @param limit - Maximum number of entries to return per page
 * @param page - Page number to fetch (starts at 1)
 * @param query - Optional MongoDB style query to filter results
 * @param sort - Optional MongoDB style sort to sort results
 * @returns Promise that resolves to array of BuilderContent entries for the requested page
 * @example
 * // Get first 10 blog posts
 * const firstPage = await getEntryByPage(builder, "blog", 10, 1);
 *
 * // Get second page with query filter
 * const secondPage = await getEntryByPage(builder, "blog", 10, 2, {
 *   data: { category: "news" }
 * });
 */
export const getEntryByPage = async (
  builder: Builder,
  model:
    | "blog"
    | "event-post"
    | "class"
    | "class-type"
    | "event-type"
    | "knowledge-base"
    | "knowledge-document-type",
  limit: number,
  page: number,
  query?: unknown,
  sort?: { [key: string]: 1 | -1 }
) => {
  const offset = (page - 1) * limit;

  const contents = await builder.getAll(model, {
    limit: limit,
    offset: offset,
    cachebust: true,
    prerender: false,
    query: query,
    sort: sort,
    enrich: true,
    options: {
      enrich: true,
      noTargeting: true,
      sort: sort,
    },
  });

  return contents;
};

export const getLatestBlogPost = async (
  builder: Builder,
  type: "Families" | "Professionals"
): Promise<BuilderContent[]> => {
  const blogs = await getEntryByPage(
    builder,
    "blog",
    1,
    1,
    {
      data: {
        $or: [
          {
            resourceType: type,
          },
          {
            resourceType: "Both",
          },
        ],
      },
    },
    {
      lastUpdated: -1,
    }
  );

  return blogs;
};

export const getBlogPostByTitleSlugTags = async (
  builder: Builder,
  searchTerm: string,
  type: "Families" | "Professionals"
): Promise<BuilderContent[]> => {
  const blogs = await getEntryByPage(
    builder,
    "blog",
    10,
    1,
    {
      data: {
        $and: [
          {
            $or: [
              { title: { $regex: searchTerm, $options: "i" } },
              { slug: { $regex: searchTerm, $options: "i" } },
              { tags: { $regex: searchTerm, $options: "i" } },
            ],
          },
          {
            $or: [{ resourceType: type }, { resourceType: "Both" }],
          },
        ],
      },
    },
    {
      lastUpdated: -1,
    }
  );

  return blogs;
};

export const getAllCategories = async (builder: Builder) => {
  const categories = await builder.getAll("blog-category", {
    limit: 1000,
    cachebust: true,
    prerender: false,
    options: {
      enrich: true,
      noTargeting: true,
    },
    query: {
      data: {
        showCategory: true,
      },
    },
  });
  return categories;
};

export const getBlogPagination = async (
  builder: Builder,
  type: "Families" | "Professionals",
  category: string,
  sort: string,
  search: string,
  excludeId?: string
) => {
  let allBlogs: BuilderContent[] = [];
  let offset = 0;
  let hasMore = true;

  while (hasMore) {
    const blogs = await builder.getAll("blog", {
      limit: 100,
      offset: offset,
      query: {
        data: {
          $and: [
            ...(search
              ? [
                  {
                    $or: [
                      { title: { $regex: search, $options: "i" } },
                      { slug: { $regex: search, $options: "i" } },
                      { tags: { $regex: search, $options: "i" } },
                    ],
                  },
                ]
              : []),
            {
              $or: [{ resourceType: type }, { resourceType: "Both" }],
            },
            {
              "blogPostCategory.value.data.label": {
                $regex: category,
                $options: "i",
              },
            },
          ],
        },
        ...(excludeId && {
          id: { $ne: excludeId },
        }),
      },
      sort: {
        ...(sort === "date_asc" && { lastUpdated: 1 }),
        ...(sort === "date_desc" && { lastUpdated: -1 }),
        ...(sort === "title_asc" && { "data.blogPostTitle": 1 }),
        ...(sort === "title_desc" && { "data.blogPostTitle": -1 }),
      },
      options: {
        enrich: true,
        noTargeting: true,
      },
      enrich: true,
      cachebust: true,
    });

    if (blogs.length === 0) {
      hasMore = false;
    } else {
      allBlogs = [...allBlogs, ...blogs];
      offset += 100;
    }
  }

  return allBlogs;
};

export const getBlogPostBySearchTypeCategorySortPage = async (
  builder: Builder,
  type: "Families" | "Professionals",
  category: string,
  sort: string,
  search: string,
  page: number,
  excludeId?: string
) => {
  const blogs = await getEntryByPage(
    builder,
    "blog",
    12,
    page,
    {
      data: {
        $and: [
          ...(search
            ? [
                {
                  $or: [
                    { title: { $regex: search, $options: "i" } },
                    { slug: { $regex: search, $options: "i" } },
                    { tags: { $regex: search, $options: "i" } },
                  ],
                },
              ]
            : []),
          {
            $or: [{ resourceType: type }, { resourceType: "Both" }],
          },
          {
            "blogPostCategory.value.data.label": {
              $regex: category,
              $options: "i",
            },
          },
        ],
      },
      ...(excludeId && {
        id: { $ne: excludeId },
      }),
    },
    {
      ...(sort === "date_asc" && { lastUpdated: 1 }),
      ...(sort === "date_desc" && { lastUpdated: -1 }),
      ...(sort === "title_asc" && { "data.blogPostTitle": 1 }),
      ...(sort === "title_desc" && { "data.blogPostTitle": -1 }),
    }
  );

  return blogs;
};

export const getRecentArticlesExcept = async (
  builder: Builder,
  excludeId: string
) => {
  const blogs = await getEntryByPage(
    builder,
    "blog",
    3,
    1,
    {
      ...(excludeId && {
        id: { $ne: excludeId },
      }),
    },
    {
      lastUpdated: -1,
    }
  );

  return blogs;
};

export const getRelatedArticles = async (builder: Builder, blogId: string) => {
  const currentBlog = await builder
    .get("blog", {
      query: {
        id: blogId,
      },
      options: {
        includesRef: true,
      },
    })
    .toPromise();

  const category =
    currentBlog?.data?.blogPostCategory?.value?.data?.label || "";

  const blogs = await getEntryByPage(
    builder,
    "blog",
    6,
    1,
    {
      id: { $ne: blogId },
      data: {
        $or: [
          {
            "blogPostCategory.value.data.label": {
              $regex: category,
              $options: "i",
            },
          },
          {
            tags: {
              $in: currentBlog?.data?.tags || [],
            },
          },
          {
            $or: [
              { resourceType: "Both" },
              { resourceType: currentBlog?.data?.resourceType },
            ],
          },
        ],
      },
    },
    {
      lastUpdated: -1,
    }
  );

  return blogs;
};

export const getRecentClassesExcept = async (
  builder: Builder,
  type: "Families" | "Professionals",
  excludeId: string
) => {
  const classes = await getEntryByPage(
    builder,
    "class",
    3,
    1,
    {
      ...(excludeId && {
        id: { $ne: excludeId },
      }),
      data: {
        $or: [{ resourceType: type }, { resourceType: "Both" }],
      },
    },
    {
      lastUpdated: -1,
    }
  );

  return classes;
};

export const getRecentEventPostsExcept = async (
  builder: Builder,
  type: "Families" | "Professionals",
  excludeId: string
) => {
  const events = await getEntryByPage(
    builder,
    "event-post",
    3,
    1,
    {
      data: {
        $or: [{ resourceType: type }, { resourceType: "Both" }],
      },
      ...(excludeId && {
        id: { $ne: excludeId },
      }),
    },
    {
      lastUpdated: -1,
    }
  );

  return events;
};

export const getRecentKnowledgeBaseExcept = async (
  builder: Builder,
  type: "Families" | "Professionals",
  excludeId: string
) => {
  const articles = await getEntryByPage(
    builder,
    "knowledge-base",
    5,
    1,
    {
      ...(excludeId && {
        id: { $ne: excludeId },
      }),
      data: {
        $or: [{ resourceType: type }, { resourceType: "Both" }],
      },
    },
    {
      lastUpdated: -1,
    }
  );

  return articles;
};

export const getLatestEventPost = async (
  builder: Builder,
  type: "Families" | "Professionals"
): Promise<BuilderContent[]> => {
  const events = await getEntryByPage(
    builder,
    "event-post",
    1,
    1,
    {
      data: {
        $or: [
          {
            resourceType: type,
          },
          {
            resourceType: "Both",
          },
        ],
      },
    },
    {
      firstPublished: -1,
    }
  );

  return events;
};

export const getEventsByPage = async (
  builder: Builder,
  type: "Families" | "Professionals",
  page: number,
  excludeId?: string
) => {
  const events = await getEntryByPage(
    builder,
    "event-post",
    12,
    page,
    {
      data: {
        $or: [{ resourceType: type }, { resourceType: "Both" }],
      },
      ...(excludeId && {
        id: { $ne: excludeId },
      }),
    },
    {
      firstPublished: -1,
    }
  );

  return events;
};

export const getEventPostPagination = async (
  builder: Builder,
  type: "Families" | "Professionals",
  excludeId?: string
) => {
  let allEvents: BuilderContent[] = [];
  let offset = 0;
  let hasMore = true;

  while (hasMore) {
    const events = await builder.getAll("event-post", {
      limit: 100,
      offset: offset,
      query: {
        data: {
          $or: [{ resourceType: type }, { resourceType: "Both" }],
        },
        ...(excludeId && {
          id: { $ne: excludeId },
        }),
      },
      options: {
        enrich: true,
        noTargeting: true,
      },
      enrich: true,
      cachebust: true,
    });

    if (events.length === 0) {
      hasMore = false;
    } else {
      allEvents = [...allEvents, ...events];
      offset += 100;
    }
  }

  return allEvents;
};

export const getLatestClassPost = async (
  builder: Builder,
  type: "Families" | "Professionals"
): Promise<BuilderContent[]> => {
  const classes = await getEntryByPage(
    builder,
    "class",
    1,
    1,
    {
      data: {
        $or: [
          {
            resourceType: type,
          },
          {
            resourceType: "Both",
          },
        ],
      },
    },
    {
      firstPublished: -1,
    }
  );

  return classes;
};

export const getClassesByPage = async (
  builder: Builder,
  type: "Families" | "Professionals",
  page: number,
  excludeId?: string
) => {
  const classes = await getEntryByPage(
    builder,
    "class",
    12,
    page,
    {
      data: {
        $or: [{ resourceType: type }, { resourceType: "Both" }],
      },
      ...(excludeId && {
        id: { $ne: excludeId },
      }),
    },
    {
      firstPublished: -1,
    }
  );

  return classes;
};

export const getClassPostPagination = async (
  builder: Builder,
  type: "Families" | "Professionals",
  excludeId?: string
) => {
  let allClasses: BuilderContent[] = [];
  let offset = 0;
  let hasMore = true;

  while (hasMore) {
    const classes = await builder.getAll("class", {
      limit: 100,
      offset: offset,
      query: {
        data: {
          $or: [{ resourceType: type }, { resourceType: "Both" }],
        },
        ...(excludeId && {
          id: { $ne: excludeId },
        }),
      },
      options: {
        enrich: true,
        noTargeting: true,
      },
      enrich: true,
      cachebust: true,
    });

    if (classes.length === 0) {
      hasMore = false;
    } else {
      allClasses = [...allClasses, ...classes];
      offset += 100;
    }
  }

  return allClasses;
};

export const getKnowledgeBaseByPage = async (
  builder: Builder,
  _: "Families" | "Professionals",
  page: number,
  limit: number,
  excludeId?: string
) => {
  const knowledgeBase = await getEntryByPage(
    builder,
    "knowledge-base",
    limit,
    page,
    {
      ...(excludeId && {
        id: { $ne: excludeId },
      }),
    },
    {
      firstPublished: -1,
    }
  );

  return knowledgeBase;
};

export const getKnowledgeBasePagination = async (
  builder: Builder,
  type: "Families" | "Professionals",
  excludeId?: string
) => {
  let allKnowledgeBase: BuilderContent[] = [];
  let offset = 0;
  let hasMore = true;

  while (hasMore) {
    const knowledgeBase = await builder.getAll("knowledge-base", {
      limit: 100,
      offset: offset,
      query: {
        data: {
          $and: [
            {
              $or: [{ resourceType: type }, { resourceType: "Both" }],
            },
          ],
        },
        ...(excludeId && {
          id: { $ne: excludeId },
        }),
      },
      options: {
        enrich: true,
        noTargeting: true,
      },
      enrich: true,
      cachebust: true,
    });

    if (knowledgeBase.length === 0) {
      hasMore = false;
    } else {
      allKnowledgeBase = [...allKnowledgeBase, ...knowledgeBase];
      offset += 100;
    }
  }

  return allKnowledgeBase;
};
